import React, { useCallback, useEffect, useState } from 'react';
import CopyButton from '@components/CopyButton';
import Copy from '@components/Icons/Copy';
import { Select } from '@components/DesignLibrary/Atoms';
import Button from '@components/DesignLibrary/Button';
import { useWatch } from 'react-hook-form';
import { useQuestionnaireFormContext } from '@/components/Questionnaire/QuestionnaireProvider';
import s from './EditListItemModal.module.scss';

type Props = {
  rowIndex: number;
  linkSourceFieldKey: string;
};

const ModalFormActions: React.FC<Props> = ({
  rowIndex,
  linkSourceFieldKey,
}) => {
  const [markingAsDuplicate, setMarkingAsDuplicate] = useState(false);
  const {
    formMethods,
    setValue,
  } = useQuestionnaireFormContext();

  const currentValues = useWatch({
    control: formMethods.control,
    name: linkSourceFieldKey,
  }) as Array<Record<string, unknown>>;

  const modalFieldId = useWatch({
    control: formMethods.control,
    name: `${linkSourceFieldKey}[${rowIndex}]._id`,
  });

  const modalFieldAssociatedWithId = useWatch({
    control: formMethods.control,
    name: `${linkSourceFieldKey}[${rowIndex}]._associatedWithId`,
  });

  // @TODO - useWatch (or useWatchFields) instead of watch
  type ListResponse = { _id: string; _associatedWithId: string | null };
  const [listResponses, setListResponses] = useState<ListResponse[]>(formMethods.watch(linkSourceFieldKey) || []);
  const watcher = formMethods.watch(linkSourceFieldKey);
  useEffect(() => {
    setListResponses(watcher || []);
  },[watcher]);

  const handleSelectDuplicate = useCallback((duplicateId) => {
    const associatedWithId = duplicateId === 'no' ? null : duplicateId;
    // @TODO - see if we can just do setValue(list[modalOpen]._associatedWithId, associatedWithId)
    setValue({
      key: linkSourceFieldKey,
      value: currentValues.map((subField, idx) => ({
        ...subField,
        _associatedWithId: idx === rowIndex ? associatedWithId : subField._associatedWithId,
      })),
    });
  }, [linkSourceFieldKey, currentValues, rowIndex, setValue]);

  return (
    <div className={s.fieldId}>
      <div className={s.fieldIdData}>
        <div>
          <p className="caption">Field ID</p>
          <p className="caption">{modalFieldId}</p>
        </div>
        {listResponses.length > 1 && (
          <Button
            size='sm'
            variant='secondary'
            onClick={() => setMarkingAsDuplicate(prev => !prev)}
            className={modalFieldAssociatedWithId ? undefined : s.markAsDuplicate}
          >
            {markingAsDuplicate ? 'Hide' : `Mark${modalFieldAssociatedWithId ? 'ed' : ''} as duplicate`}
          </Button>
        )}
        <CopyButton text={modalFieldId} className={s.copyIDButton}>
          <Copy size={16} /> Copy
        </CopyButton>
      </div>
      {markingAsDuplicate && (
        <Select
          compact
          label="This field is a duplicate of:"
          options={[
            {
              key: 'no',
              label: 'None',
            },
            ...(listResponses)
              .filter(subField => subField?._id !== listResponses[rowIndex]?._id && !subField._associatedWithId)
              .map(subField => ({
                key: subField._id,
                label: subField._id,
              })),
          ]}
          optional
          placeholder='None'
          value={modalFieldAssociatedWithId}
          onChange={handleSelectDuplicate}
        />
      )}
    </div>
  );
};

export default ModalFormActions;